import React, { useEffect, useRef, useContext, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { MdSecurity, MdClose, MdLightMode, MdDarkMode } from 'react-icons/md';
import { FaCheckCircle } from 'react-icons/fa';
import styles from './LandingPage.module.css';
import RegisterPage from './RegisterPage';
import LoginPage from './LoginPage';
import Logo from '../../components/Logo';

const animatedTexts = [
  'Trade in Stocks',
  'Trade in Options',
  'Invest in Mutual Funds',
  'Trade in Stocks',
];

const LandingPage = () => {
  const canvasRef = useRef(null);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [activeModal, setActiveModal] = useState(null); // 'login', 'register', or null
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 2,
      d: Math.random() * 0.5 + 0.2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Use colors consistent with theme.css variables
      ctx.fillStyle = theme === 'dark' ? '#b7b7b757' : '#15689554';
      
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        
        p.y += p.d;
        if (p.y > canvas.height) {
          p.y = -10;
          p.x = Math.random() * canvas.width;
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [theme]);

  const openRegister = useCallback(() => {
    setActiveModal('register');
  }, []);

  const openLogin = useCallback(() => {
    setActiveModal('login');
  }, []);

  const closeModals = useCallback(() => {
    setActiveModal(null);
    navigate('/landing', { replace: true, state: {} });
  }, [navigate]);

  useEffect(() => {
    document.body.style.overflow = activeModal ? 'hidden' : 'unset';
  }, [activeModal]);

  useEffect(() => {
    if (location.state?.openLogin) {
      openLogin();
    } else if (location.state?.openRegister) {
      openRegister();
    }
  }, [location.state, openLogin, openRegister]);

  return (
    <div className={`${styles.landingWrapper} ${activeModal ? styles.modalOpen : ''}`}>
      <canvas ref={canvasRef} className={styles.particles}></canvas>

      <header className={styles.navbar}>
        <div className={styles.logo}>
          <Logo size="1.8rem" />
        </div>
        <div className={styles.trustBadge}>
          <MdSecurity /> Trusted by 2 Lakhs+ Traders
        </div>
      </header>

      <main className={styles.mainContent}>
        {activeModal ? (
          <div className={styles.modalOverlay} onClick={closeModals}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <button className={styles.closeModalBtn} onClick={closeModals}>
                <MdClose />
              </button>
              {activeModal === 'register' ? (
                <RegisterPage onSwitch={openLogin} />
              ) : (
                <LoginPage onSwitch={openRegister} />
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.navDivider}></div>
            <section className={styles.hero}>
              <div className={styles.heroContent}>
                <div className={styles.heroText}>
                  <h1>
                    Premium Trading <span>For High Growth</span>
                  </h1>
                  <p className={styles.heroSubtext}>
                    Get hand-holding support and wealth increment guidance with most trusted leverage platform.
                  </p>

                  <div className={styles.rollingContainer}>
                    <div className={styles.rollingWrapper}>
                      <div className={styles.rollingList}>
                        {animatedTexts.map((text, i) => (
                          <div key={i} className={styles.rollingItem}>
                            <FaCheckCircle /> {text}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className={styles.cta}>
                    <button className={styles.gold} onClick={openRegister}>Get Started</button>
                    <button className={`${styles.primary} ${styles.loginBtn}`} onClick={openLogin}>
                      <span className={styles.btnSmallText}>Already a customer?</span>
                      <span className={styles.btnMainText}>Login</span>
                    </button>
                  </div>
                </div>

                <div className={styles.heroImageContainer}>
                  <img src="/hero.png" alt="Platform Preview" className={styles.heroImage} />
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className={styles.footer}>
        <p>
          &copy; {new Date().getFullYear()} {process.env.REACT_APP_NAME}. All rights reserved. 
          <span className={styles.footerDivider}>|</span>
          Theme 
          <button className={styles.themeToggleBtn} onClick={toggleTheme}>
            {theme === 'dark' ? <MdDarkMode /> : <MdLightMode />}
          </button>
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;
